import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CardsAPI from 'custom/dashboard/api/cards';
import PipelinesAPI from 'custom/dashboard/api/pipelines';

// The card of the detail page, its pipeline's stages and its history. Every change goes
// through the API and the page then shows the card the server answered with, so the page
// never drifts from what the board and the conversation see.
export function useCardDetail() {
  const { t } = useI18n();
  const card = ref(null);
  const stages = ref([]);
  const activities = ref([]);
  const isNotFound = ref(false);
  // Whether the card itself was changed here (comments do not change it).
  const isChanged = ref(false);

  const loadActivities = async () => {
    const { data } = await CardsAPI.getActivities(card.value.id);
    activities.value = data.payload;
  };

  const loadStages = async () => {
    const { data } = await PipelinesAPI.getStages(card.value.pipeline_id);
    stages.value = data;
  };

  const load = async id => {
    try {
      const { data } = await CardsAPI.show(id);
      card.value = data;
    } catch {
      isNotFound.value = true;
      return;
    }
    // Settled independently: a failing history must not leave the stage tracker empty.
    const results = await Promise.allSettled([loadStages(), loadActivities()]);
    if (results.some(result => result.status === 'rejected'))
      useAlert(t('CARDS.SHOW.LOAD_ERROR'));
  };

  // A task change touches the card as the board sees it (its task summary) and its history, so
  // both come back from the server. Resolves to whether the change was saved, so a dialog only
  // closes on success.
  const applyTask = async request => {
    try {
      await request();
      isChanged.value = true;
      const { data } = await CardsAPI.show(card.value.id);
      card.value = data;
      await loadActivities();
      return true;
    } catch {
      useAlert(t('CARDS.TASKS.UPDATE_ERROR'));
      return false;
    }
  };

  const createTask = task =>
    applyTask(() => CardsAPI.createTask(card.value.id, task));

  const updateTask = (taskId, task) =>
    applyTask(() => CardsAPI.updateTask(card.value.id, taskId, task));

  const changeTaskStatus = (taskId, transition) =>
    applyTask(() =>
      CardsAPI.changeTaskStatus(card.value.id, taskId, transition)
    );

  const deleteTask = taskId =>
    applyTask(() => CardsAPI.deleteTask(card.value.id, taskId));

  // `visit` is { scheduledAt, assigneeId }.
  const scheduleVisit = (taskId, visit) =>
    applyTask(() => CardsAPI.scheduleVisit(card.value.id, taskId, visit));

  // Each change may add to the history (a stage entry, a resolution), so it is reloaded too.
  const apply = async request => {
    try {
      const { data } = await request();
      card.value = data;
      isChanged.value = true;
      await loadActivities();
    } catch {
      useAlert(t('CARDS.SHOW.UPDATE_ERROR'));
    }
  };

  const moveTo = stageId => {
    if (stageId === card.value.stage_id) return null;
    return apply(() => CardsAPI.move(card.value.id, { stageId }));
  };

  const setPriority = priority =>
    apply(() => CardsAPI.update(card.value.id, { priority }));

  const toggleAssignee = userId => {
    const ids = card.value.assignees.map(assignee => assignee.id);
    const assigneeIds = ids.includes(userId)
      ? ids.filter(id => id !== userId)
      : [...ids, userId];
    return apply(() =>
      CardsAPI.update(card.value.id, { assignee_ids: assigneeIds })
    );
  };

  // null clears the team.
  const setTeam = teamId =>
    apply(() => CardsAPI.update(card.value.id, { team_id: teamId }));

  // Labels are stored by title, as everywhere else in Chatwoot.
  const toggleLabel = title => {
    const { labels } = card.value;
    return apply(() =>
      CardsAPI.update(card.value.id, {
        labels: labels.includes(title)
          ? labels.filter(label => label !== title)
          : [...labels, title],
      })
    );
  };

  // The status changes on its own: the card stays on the step it is on.
  const resolve = () => apply(() => CardsAPI.resolve(card.value.id));

  const cancel = () => apply(() => CardsAPI.cancel(card.value.id));

  const reopen = () => apply(() => CardsAPI.reopen(card.value.id));

  // Resolves to whether the card was deleted, so the confirmation only closes on success.
  const deleteCard = async () => {
    try {
      await CardsAPI.delete(card.value.id);
      isChanged.value = true;
      return true;
    } catch {
      useAlert(t('CARDS.SHOW.DELETE_ERROR'));
      return false;
    }
  };

  // `comment` is { content, files }.
  const addComment = async comment => {
    try {
      const { data } = await CardsAPI.createComment(card.value.id, comment);
      activities.value.push(data);
      return true;
    } catch {
      useAlert(t('CARDS.SHOW.COMMENT_ERROR'));
      return false;
    }
  };

  // Both resolve to whether the change was saved, so an edit box only closes on success.
  const editComment = async (activityId, content) => {
    try {
      const { data } = await CardsAPI.updateComment(
        card.value.id,
        activityId,
        content
      );
      activities.value = activities.value.map(activity =>
        activity.id === activityId ? data : activity
      );
      return true;
    } catch {
      useAlert(t('CARDS.SHOW.COMMENT_UPDATE_ERROR'));
      return false;
    }
  };

  const deleteComment = async activityId => {
    try {
      await CardsAPI.deleteComment(card.value.id, activityId);
      activities.value = activities.value.filter(
        activity => activity.id !== activityId
      );
      return true;
    } catch {
      useAlert(t('CARDS.SHOW.COMMENT_DELETE_ERROR'));
      return false;
    }
  };

  return {
    card,
    stages,
    activities,
    isNotFound,
    isChanged,
    load,
    moveTo,
    setPriority,
    toggleAssignee,
    setTeam,
    toggleLabel,
    resolve,
    cancel,
    reopen,
    deleteCard,
    addComment,
    editComment,
    deleteComment,
    createTask,
    updateTask,
    changeTaskStatus,
    deleteTask,
    scheduleVisit,
  };
}
